'use client';
import { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useExperiment } from '@web/experiments';
import { flagshipABContentNotFound } from '@web/components/FlagshipABTest/events';
import { useReporter } from '../reporter';
import { MarketingHeader } from './MarketingHeader';
import { schema as flagshipAbHeaderSchema } from './builders/flagship_ab_header';
import { schema as headerSchema } from './builders/header';

export const FlagshipHeader = ({ content }) => {
  const reporter = useReporter();

  const testContent = useMemo(() => {
    try {
      return flagshipAbHeaderSchema.validateSync(content).content;
    } catch {
      return null;
    }
  }, [content]);

  const { value: experimentValue = 'control' } = useExperiment(
    testContent?.test_name ?? null,
  );

  const resolvedHeader = useMemo(() => {
    if (!testContent) {
      return null;
    }

    const matchedVariant = testContent.variants?.find(
      variant => variant.variant_name === experimentValue,
    );

    return matchedVariant?.blocks?.[0] ?? null;
  }, [testContent, experimentValue]);

  useEffect(() => {
    if (testContent && !resolvedHeader) {
      reporter.tag(
        flagshipABContentNotFound(
          testContent.test_name,
          experimentValue || 'Not Found',
        ),
      );
    }
  }, [testContent, resolvedHeader, experimentValue, reporter]);

  const headerProps = useMemo(() => {
    if (!resolvedHeader) {
      return null;
    }

    try {
      return headerSchema.validateSync(resolvedHeader);
    } catch {
      return null;
    }
  }, [resolvedHeader]);

  if (!headerProps) {
    return null;
  }

  return <MarketingHeader {...headerProps} />;
};

FlagshipHeader.propTypes = {
  content: PropTypes.object.isRequired,
};
